import { useRef, useCallback, useEffect } from 'react';
import { PlayerConfig, PlaybackConfig, ProgressPayload } from '../types/player';

// Import core hooks
import { useVideoElement } from './core/use-video-element';
import { usePlayerState } from './core/use-player-state';
import { usePlayerEvents } from './core/use-player-events';

// Import feature hooks
import { usePlaybackControl } from './features/use-playback-control';
import { useTimeTracking } from './features/use-time-tracking';
import { useVolumeControl } from './features/use-volume-control';
import { useFullscreenControl } from './features/use-fullscreen-control';
import { useControlsVisibility } from './features/use-controls-visibility';
import { useLoopControl } from './features/use-loop-control';
import { useStoryboardPreview } from './features/use-storyboard-preview';
import { useEndScreen } from './features/use-end-screen';

// Import integration hooks
import { useProgressTracking } from './integration/use-progress-tracking';

export interface UsePlayerProps extends PlayerConfig {
  playbackConfig?: PlaybackConfig;
  onNextVideo: () => void;
  onProgressSave?: (progress: ProgressPayload) => void;
}

export const usePlayer = ({
  autoPlay = true,
  lessonDocumentId,
  playbackConfig,
  onNextVideo,
  onProgressSave,
}: UsePlayerProps) => {
  // Container ref for fullscreen
  const containerRef = useRef<HTMLDivElement>(null);

  // Core hooks
  const { playerRef, videoElement } = useVideoElement();
  const {
    isInitialized,
    setIsInitialized,
    isPlaying,
    setIsPlaying,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    isBuffering,
    setIsBuffering,
    autoPlayAttemptedRef,
    resetPlayerState,
  } = usePlayerState(videoElement);

  // Fullscreen control (needed by loop control)
  const {
    isFullscreen,
    isInFullscreen,
    shouldRestoreFullscreen,
    setIsInFullscreen,
    setShouldRestoreFullscreen,
    handleFullscreen,
    handleCustomFullscreen,
    exitFullscreenTemporarily,
    restoreFullscreen,
  } = useFullscreenControl(playerRef);

  // Loop control
  const {
    loopStart,
    loopEnd,
    isLoopModalOpen,
    loopEnabled,
    setLoopStart,
    setLoopEnd,
    openLoopModal,
    closeLoopModal,
    clearLoop,
  } = useLoopControl(exitFullscreenTemporarily, restoreFullscreen);

  // Controls visibility
  const {
    showControls,
    setMouseOverControls,
    showPlayerControls,
    hidePlayerControls,
    resetControlsTimeout,
    handleUserInteractionStart,
    handleUserInteractionEnd,
  } = useControlsVisibility(isPlaying, isFullscreen, isLoopModalOpen);

  const { saveLessonProgress, trackProgress, trackSeekProgress, handleToggleCompletion } =
    useProgressTracking(videoElement, currentTime, duration, lessonDocumentId, onProgressSave);

  // Create a minimal handleSeek function before useEndScreen
  const handleSeekTemp = useCallback(
    (newTime: number) => {
      if (!videoElement) return;
      videoElement.currentTime = newTime;
      setCurrentTime(newTime);
      if (trackSeekProgress) {
        trackSeekProgress(newTime);
      }
    },
    [videoElement, setCurrentTime, trackSeekProgress],
  );

  // End screen
  const { showEndScreen, setShowEndScreen, countdown, handleEnded, handleRestart, clearCountdown } =
    useEndScreen(videoElement, setIsPlaying, handleSeekTemp, saveLessonProgress, onNextVideo);

  // Full handleSeek with all dependencies available
  const handleSeek = useCallback(
    (newTime: number, isUserInitiated = true) => {
      if (!videoElement) return;

      videoElement.currentTime = newTime;
      setCurrentTime(newTime);

      // Track progress on seek
      if (trackSeekProgress) {
        trackSeekProgress(newTime);
      }
    },
    [videoElement, setCurrentTime, trackSeekProgress],
  );

  const {
    formatTime: timeFormatter,
    seekTo,
    lastSeekTimeRef,
    isUserSeekRef,
  } = useTimeTracking(
    videoElement,
    setCurrentTime,
    setDuration,
    loopStart,
    loopEnd,
    loopEnabled,
    handleSeek,
  );

  // Volume control
  const { volume, isMuted, handleVolumeChange, toggleMute } = useVolumeControl(videoElement);
  // Storyboard preview
  const {
    storyboard,
    hoverPreview,
    setHoverPreview,
    fetchStoryboardMetadata,
    handleTimelineHover,
    hidePreview,
  } = useStoryboardPreview(duration);

  // Playback control
  const {
    playbackSpeed,
    currentAction,
    showToggleIcon,
    togglePlayPause,
    handleRewind,
    handleForward,
    handlePlaybackSpeedChange,
    handleContainerClick,
    attemptAutoplay,
  } = usePlaybackControl(
    videoElement,
    isInitialized,
    autoPlay,
    autoPlayAttemptedRef,
    setIsPlaying,
    hidePreview,
  );

  // New handler for video container taps to manage control visibility
  const handleVideoContainerTap = useCallback(() => {
    if (!showControls) {
      showPlayerControls(); // Show controls
      resetControlsTimeout(); // And set them to hide again after a delay
      // Do not toggle play/pause on this tap, the goal was to show controls.
    } else {
      // If controls are already visible, then a tap toggles play/pause.
      handleContainerClick(); // This is the original function from usePlaybackControl
    }
  }, [showControls, showPlayerControls, resetControlsTimeout, handleContainerClick]);

  // Fetch storyboard metadata when available
  useEffect(() => {
    if (storyboard === null && playbackConfig?.storyboardMetadataUrl) {
      fetchStoryboardMetadata(playbackConfig.storyboardMetadataUrl);
    }
  }, [playbackConfig, storyboard, fetchStoryboardMetadata]);

  // Set up media event handlers
  const eventHandlers = {
    onPlay: () => {
      setIsPlaying(true);
    },
    onPause: () => {
      setIsPlaying(false);
    },
    onTimeUpdate: () => {
      trackProgress();
    },
    onDurationChange: () => {
      if (videoElement && !isNaN(videoElement.duration) && isFinite(videoElement.duration)) {
        setDuration(videoElement.duration);
      }
    },
    onWaiting: () => setIsBuffering(true),
    onPlaying: () => setIsBuffering(false),
    onEnded: handleEnded,
    onLoadedMetadata: () => {
      if (videoElement && !isNaN(videoElement.duration) && isFinite(videoElement.duration)) {
        setDuration(videoElement.duration);
        setIsInitialized(true);
      }
    },
    onCanPlay: () => {
      if (autoPlay && !autoPlayAttemptedRef.current && isInitialized) {
        //  attemptAutoplay(); Removed in order to favor playback control showing
      }
    },
  };

  // Register event handlers
  usePlayerEvents(videoElement, playerRef, eventHandlers);

  return {
    // Refs
    playerRef,
    containerRef,
    videoElement,

    // Core state
    isInitialized,
    isPlaying,
    currentTime,
    duration,
    isBuffering,

    // Video controls
    togglePlayPause,
    handleSeek,
    playbackSpeed,
    handlePlaybackSpeedChange,
    volume,
    isMuted,
    handleVolumeChange,
    toggleMute,
    formatTime: timeFormatter,

    // Controls visibility
    showControls,
    showToggleIcon,
    showPlayerControls,
    hidePlayerControls,
    resetControlsTimeout,
    handleUserInteractionStart,
    handleUserInteractionEnd,
    handleContainerClick,
    handleVideoContainerTap,
    setMouseOverControls,

    // Fullscreen
    isFullscreen,
    handleFullscreen,
    isInFullscreen,
    setIsInFullscreen,
    shouldRestoreFullscreen,
    setShouldRestoreFullscreen,
    handleCustomFullscreen,

    // Loop control
    loopStart,
    loopEnd,
    loopEnabled,
    isLoopModalOpen,
    openLoopModal,
    closeLoopModal,
    setLoopStart,
    setLoopEnd,
    clearLoop,

    // Navigation
    handleRewind,
    handleForward,
    currentAction,

    // Storyboard preview features
    storyboard,
    hoverPreview,
    setHoverPreview,
    handleTimelineHover,
    hidePreview,

    // End screen
    showEndScreen,
    countdown,
    clearCountdown,
    handleRestart,

    // Reset player state
    resetPlayerState,

    // Progress management
    handleToggleCompletion,
    saveLessonProgress,
    trackProgress,
    trackSeekProgress,
  };
};
